
import {
  computed, defineComponent, PropType, ref,
} from 'vue'
import { User, Message } from '@/types/webinar.d'
import IconTrash from '@/vue/icons/IconTrash.vue'
import IconUserSlash from '@/vue/icons/IconUserSlash.vue'
import axios from 'axios'
import { useStore } from 'vuex'
import { key } from '@/vue/store'
import { useI18n } from 'vue-i18n'
import IconCheck from '@/vue/icons/IconCheck.vue'

export default defineComponent({
  name: 'ChatMessage',
  props: {
    message: {
      type: Object as PropType<Message>,
      required: true,
    },
    users: {
      type: Array as PropType<User[]>,
      required: true,
    },
  },
  components: {
    IconCheck,
    IconTrash,
    IconUserSlash,
  },
  emits: ['deleted'],
  setup(props, { emit }) {
    const { t } = useI18n()

    const loading = ref(false)
    const user = computed<User | null>(() => {
      let current: User | null = null
      props.users.some((u) => {
        if (u.id === props.message.user) {
          current = u
          return true
        }
        return false
      })
      return current
    })
    const isAdmin = computed(() => (user.value ? user.value.admin : false))
    const userName = computed(() => (user.value ? (user.value.name || `user ${user.value.id}`) : '???'))

    const hours = props.message.timestamp.getHours()
    const minutes = props.message.timestamp.getMinutes()
    const seconds = props.message.timestamp.getSeconds()

    const store = useStore(key)
    const deleteMessage = async () => {
      if (store.state.userAdmin) {
        let { message } = props.message
        if (message.length > 50) {
          message = `${message.substr(0, 47)}...`
        }
        // eslint-disable-next-line no-alert
        if (confirm(t('public.confirmRemoving', {
          user: userName.value,
          message,
        }))) {
          loading.value = true
          await axios.delete(`/messages/${props.message.id}`)
          emit('deleted')
        }
      }
    }

    const banUser = async () => {
      // eslint-disable-next-line no-alert
      if (store.state.userAdmin && confirm(t('public.confirmRemoving', {
        user: userName.value,
      }))) {
        loading.value = true
        await axios.put(`/users/${props.message.user}/ban`)
        emit('deleted')
      }
    }

    const currentUserAdmin = computed(() => store.state.userAdmin)

    const approveMessage = async () => {
      if (store.state.userAdmin) {
        let { message } = props.message
        if (message.length > 50) {
          message = `${message.substr(0, 47)}...`
        }
        // eslint-disable-next-line no-alert
        if (confirm(t('public.confirmApproving', {
          user: userName.value,
          message,
        }))) {
          loading.value = true
          const { data } = await axios.post(`/messages/${props.message.id}/approve`)
          if (data.success) {
            emit('deleted')
          }
        }
      }
    }

    const text = computed(() => props.message.message
      .replace(/[&<>]/g, (e) => {
        if (e === '&') {
          return '&amp;'
        }
        return e === '<' ? '&lt;' : '&gt;'
      })
      .replace(/((http|https|ftp):\/\/[\w?=&./-;#~%-]+(?![\w\s?&./;#~%"=-]*>))/g, '<a href="$1" target="_blank">$1</a>'))

    return {
      isAdmin,
      currentUserAdmin,
      userName,
      time: `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`,
      deleteMessage,
      banUser,
      text,
      approveMessage,
      t,
    }
  },
})
